import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./ErrorMemberReport.css";
import API_BASE_URL from "../RestAPI.js";
import { BarChart } from "@mui/x-charts/BarChart";
import {
  FiAlertTriangle, FiLayers, FiCode, FiCheckSquare, FiUploadCloud, FiActivity,
  FiChevronDown, FiRefreshCw, FiCalendar, FiDownload, FiCopy, FiUsers, FiClock,
  FiTrendingUp, FiTrendingDown, FiLink, FiExternalLink,
} from "react-icons/fi";

/* ───── Date helpers ───── */
const toStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayStr = () => toStr(new Date());

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "last7", label: "Last 7 Days" },
  { key: "custom", label: "Custom" },
  { key: "all", label: "All Time" },
];

// Presets the API resolves itself, from a bare ?range=. "custom" sends dates
// instead and "all" sends nothing at all.
const SERVER_RANGES = ["today", "week", "last7"];

const COLS = [
  { key: "in_dev", label: "In Dev", color: "#ff9800" },
  { key: "in_qa", label: "In QA", color: "#ffc107" },
  { key: "production", label: "Production", color: "#4caf50" },
];

const ErrorMemberReport = () => {
  const [rangeMode, setRangeMode] = useState("week");
  const [customFrom, setCustomFrom] = useState(todayStr());
  const [customTo, setCustomTo] = useState(todayStr());
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberOpen, setMemberOpen] = useState(false);
  const [upcoming, setUpcoming] = useState("");

  const [data, setData] = useState(null);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const memberBoxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (memberBoxRef.current && !memberBoxRef.current.contains(e.target)) setMemberOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const buildQuery = useCallback(() => {
    const qs = new URLSearchParams();
    if (SERVER_RANGES.includes(rangeMode)) qs.append("range", rangeMode);
    else if (rangeMode === "custom") { qs.append("from_date", customFrom); qs.append("to_date", customTo); }
    // "all" => send no date params
    if (selectedMembers.length) qs.append("member_ids", selectedMembers.join(","));
    const s = qs.toString();
    return s ? `?${s}` : "";
  }, [rangeMode, customFrom, customTo, selectedMembers]);

  /* ───── Public share link (WhatsApp / Teams) ─────
     Points at the unauthenticated report page, which serves the same numbers as
     a PNG plus the OpenGraph tags chat clients need to unfurl it. The member
     filter is deliberately dropped: the shared report is the consolidated one,
     and the public endpoint never exposes member names. */
  const shareUrl = useMemo(() => {
    const qs = new URLSearchParams();
    if (rangeMode === "custom") { qs.append("from_date", customFrom); qs.append("to_date", customTo); }
    else qs.append("range", rangeMode === "all" ? "all" : (SERVER_RANGES.includes(rangeMode) ? rangeMode : "last7"));
    return `${API_BASE_URL}error-tracking/report/?${qs.toString()}`;
  }, [rangeMode, customFrom, customTo]);

  const copyShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [shareUrl]);

  const fetchData = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/errors/member-report/${buildQuery()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      const json = await res.json();
      if (!res.ok || json.status === "fail") throw new Error(json.message || "Failed to load report");
      setData(json.data || null);
      if (json.data?.available_members?.length) setAvailableMembers(json.data.available_members);
    } catch (e) {
      setError(e.message || "Something went wrong");
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleMember = (id) => {
    const mid = String(id);
    setSelectedMembers((prev) => prev.includes(mid) ? prev.filter((v) => v !== mid) : [...prev, mid]);
  };

  const summary = useMemo(() => data?.summary || { in_dev: 0, in_qa: 0, production: 0, total: 0 }, [data]);
  const members = useMemo(() => data?.members || [], [data]);
  const period = useMemo(() => data?.period || {}, [data]);

  // Consolidated (no-member) report data
  const categoryRows = useMemo(() => data?.category_breakdown || [], [data]);
  const metrics = useMemo(() => data?.summary_metrics || {
    total_patterns: 0, moved_qa: 0, moved_prod: 0, in_development: 0, upcoming_deployment: 0,
  }, [data]);
  const catTotals = useMemo(() => categoryRows.reduce((a, r) => ({
    total_patterns: a.total_patterns + (r.total_patterns || 0),
    moved_qa: a.moved_qa + (r.moved_qa || 0),
    moved_prod: a.moved_prod + (r.moved_prod || 0),
  }), { total_patterns: 0, moved_qa: 0, moved_prod: 0 }), [categoryRows]);
  const periodTitle = useMemo(
    () => (period.from && period.to ? `from ${period.from} to ${period.to}` : "(all time)"),
    [period]
  );
  const comparison = useMemo(() => data?.comparison || null, [data]);
  const upcomingCount = Number(upcoming) || 0;  // manual, user-entered

  const periodLabel = useMemo(() => {
    if (rangeMode === "today") return "Today";
    if (rangeMode === "week") return "This Week";
    if (rangeMode === "all") return "All Time";
    return `${period.from || customFrom} to ${period.to || customTo}`;
  }, [rangeMode, period, customFrom, customTo]);

  const chart = useMemo(() => ({
    labels: members.map((m) => m.member_name),
    series: COLS.map((c) => ({ data: members.map((m) => m[c.key] || 0), label: c.label, color: c.color, stack: "s" })),
    has: members.length > 0,
  }), [members]);

  const memberFilterLabel = selectedMembers.length === 0
    ? "All Members"
    : `${selectedMembers.length} Member${selectedMembers.length > 1 ? "s" : ""}`;

  /* ───── Export: consolidated PNG rendered on a canvas ───── */
  const downloadPng = useCallback(() => {
    const dpr = 2;
    const padX = 28, padY = 24, rowH = 34, headerH = 40;
    const cols = [
      { key: "member_name", label: "Member", w: 300, align: "left" },
      { key: "in_dev", label: "In Dev", w: 110, align: "center" },
      { key: "in_qa", label: "In QA", w: 110, align: "center" },
      { key: "production", label: "Production", w: 130, align: "center" },
      { key: "total", label: "Total", w: 110, align: "center" },
    ];
    const tableW = cols.reduce((a, c) => a + c.w, 0);
    const width = tableW + padX * 2;
    const titleBlock = 58;
    const height = padY * 2 + titleBlock + headerH + rowH * (members.length + 1) + 30;

    const canvas = document.createElement("canvas");
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    // title + date range
    ctx.fillStyle = "#1a1a2e";
    ctx.font = "700 20px Segoe UI, Arial, sans-serif";
    ctx.textBaseline = "top";
    ctx.fillText("Error Pattern Handling — Team Report", padX, padY);
    ctx.fillStyle = "#666677";
    ctx.font = "13px Segoe UI, Arial, sans-serif";
    ctx.fillText(period.from && period.to ? `From ${period.from}  to  ${period.to}` : "All time",
      padX, padY + 30);

    let y = padY + titleBlock;
    // header row
    ctx.fillStyle = "#7c5cfc";
    ctx.fillRect(padX, y, tableW, headerH);
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 13px Segoe UI, Arial, sans-serif";
    ctx.textBaseline = "middle";
    let x = padX;
    for (const c of cols) {
      const cx = c.align === "left" ? x + 12 : x + c.w / 2;
      ctx.textAlign = c.align === "left" ? "left" : "center";
      ctx.fillText(c.label, cx, y + headerH / 2);
      x += c.w;
    }
    y += headerH;

    // data rows
    ctx.font = "13px Segoe UI, Arial, sans-serif";
    members.forEach((m, i) => {
      ctx.fillStyle = i % 2 === 0 ? "#f7f7fb" : "#ffffff";
      ctx.fillRect(padX, y, tableW, rowH);
      x = padX;
      for (const c of cols) {
        ctx.fillStyle = c.key === "member_name" ? "#1a1a2e" : "#333344";
        ctx.font = c.key === "total" ? "700 13px Segoe UI, Arial, sans-serif" : "13px Segoe UI, Arial, sans-serif";
        const val = c.key === "member_name" ? (m.member_name || "") : String(m[c.key] || 0);
        const cx = c.align === "left" ? x + 12 : x + c.w / 2;
        ctx.textAlign = c.align === "left" ? "left" : "center";
        ctx.fillText(val, cx, y + rowH / 2);
        x += c.w;
      }
      y += rowH;
    });

    // totals row
    ctx.fillStyle = "#ece8ff";
    ctx.fillRect(padX, y, tableW, rowH);
    x = padX;
    const totals = { member_name: "TOTAL", in_dev: summary.in_dev, in_qa: summary.in_qa, production: summary.production, total: summary.total };
    for (const c of cols) {
      ctx.fillStyle = "#4a3aa0";
      ctx.font = "700 13px Segoe UI, Arial, sans-serif";
      const val = c.key === "member_name" ? totals.member_name : String(totals[c.key] || 0);
      const cx = c.align === "left" ? x + 12 : x + c.w / 2;
      ctx.textAlign = c.align === "left" ? "left" : "center";
      ctx.fillText(val, cx, y + rowH / 2);
      x += c.w;
    }

    // border
    ctx.strokeStyle = "#d4d4dc";
    ctx.lineWidth = 1;
    ctx.strokeRect(padX, padY + titleBlock, tableW, headerH + rowH * (members.length + 1));

    const link = document.createElement("a");
    link.download = `error-member-report-${todayStr()}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }, [members, summary, period]);

  /* ───── Export: copy consolidated report as aligned text ───── */
  const copyText = useCallback(async () => {
    const cols = [
      { key: "member_name", label: "Member", w: 24 },
      { key: "in_dev", label: "In Dev", w: 8 },
      { key: "in_qa", label: "In QA", w: 8 },
      { key: "production", label: "Production", w: 12 },
      { key: "total", label: "Total", w: 8 },
    ];
    const fmt = (v, w, left) => left ? String(v).padEnd(w) : String(v).padStart(w);
    const line = (r) => cols.map((c, i) =>
      fmt(c.key === "member_name" ? (r.member_name ?? r[c.key]) : (r[c.key] ?? 0), c.w, i === 0)
    ).join("  ");

    const lines = [];
    lines.push("Error Pattern Handling — Team Report");
    lines.push(`From ${period.from || "-"} to ${period.to || "-"}`);
    lines.push("");
    lines.push(cols.map((c, i) => fmt(c.label, c.w, i === 0)).join("  "));
    lines.push("-".repeat(cols.reduce((a, c) => a + c.w + 2, 0)));
    members.forEach((m) => lines.push(line(m)));
    lines.push("-".repeat(cols.reduce((a, c) => a + c.w + 2, 0)));
    lines.push(line({ member_name: "TOTAL", ...summary }));
    const text = lines.join("\n");

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [members, summary, period]);

  /* ───── Export: CONSOLIDATED report (category + summary, no member) ───── */
  const downloadTotalsPng = useCallback(() => {
    const dpr = 2, padX = 28, padY = 24, rowH = 34, headerH = 40, gap = 34;
    // Category table
    const catCols = [
      { key: "category", label: "Category", w: 200, align: "left" },
      { key: "total_patterns", label: "Total Patterns", w: 150, align: "center" },
      { key: "moved_qa", label: "Moved to QA", w: 150, align: "center" },
      { key: "moved_prod", label: "Moved to Production", w: 190, align: "center" },
    ];
    // Summary table
    const sumCols = [
      { key: "metric", label: "Metric", w: 340, align: "left" },
      { key: "count", label: "Count", w: 190, align: "center" },
    ];
    const sumRows = [
      { metric: "Total Patterns", count: `${metrics.total_patterns}` },
      { metric: "Moved to QA", count: `${metrics.moved_qa} / ${metrics.total_patterns}` },
      { metric: "Moved to Production", count: `${metrics.moved_prod} / ${metrics.total_patterns}` },
      { metric: "In Development", count: `${metrics.in_development}`, bold: true },
      { metric: "Upcoming Deployment Plan", count: `${upcomingCount}`, bold: true },
    ];
    // Comparison table (only present in All Time mode)
    const compCols = [
      { key: "metric", label: "Metric", w: 360, align: "left" },
      { key: "count", label: "Error Patterns", w: 170, align: "center" },
    ];
    const compRows = comparison ? [
      { metric: `Last 7 days (${comparison.last_7.from} to ${comparison.last_7.to})`, count: comparison.last_7.count },
      { metric: `Previous 7 days (${comparison.prev_7.from} to ${comparison.prev_7.to})`, count: comparison.prev_7.count },
      { metric: "Change", count: `${comparison.change_pct > 0 ? "+" : ""}${comparison.change_pct}%`, bold: true },
    ] : [];

    const catW = catCols.reduce((a, c) => a + c.w, 0);
    const sumW = sumCols.reduce((a, c) => a + c.w, 0);
    const compW = compCols.reduce((a, c) => a + c.w, 0);
    const tableW = Math.max(catW, sumW, comparison ? compW : 0);
    const width = tableW + padX * 2;
    const titleBlock = 58;
    const catBlock = headerH + rowH * (categoryRows.length + 1);
    const sumHeaderH = 26;
    const sumBlock = sumHeaderH + headerH + rowH * sumRows.length;
    const compBlock = comparison ? (sumHeaderH + headerH + rowH * compRows.length) : 0;
    const height = padY * 2 + titleBlock + catBlock + gap + sumBlock
      + (comparison ? gap + compBlock : 0) + 20;

    const canvas = document.createElement("canvas");
    canvas.width = width * dpr; canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, width, height);

    // title
    ctx.fillStyle = "#1a1a2e"; ctx.textBaseline = "top";
    ctx.font = "700 20px Segoe UI, Arial, sans-serif";
    ctx.fillText("Error tracking report", padX, padY);
    ctx.fillStyle = "#666677"; ctx.font = "13px Segoe UI, Arial, sans-serif";
    ctx.fillText(period.from && period.to ? `from ${period.from} to ${period.to}` : "all time",
      padX + 210, padY + 5);

    const drawTable = (cols, rows, startY, footer) => {
      const w = cols.reduce((a, c) => a + c.w, 0);
      let y = startY;
      // header
      ctx.fillStyle = "#7c5cfc"; ctx.fillRect(padX, y, w, headerH);
      ctx.fillStyle = "#ffffff"; ctx.textBaseline = "middle";
      ctx.font = "700 13px Segoe UI, Arial, sans-serif";
      let x = padX;
      for (const c of cols) {
        ctx.textAlign = c.align === "left" ? "left" : "center";
        ctx.fillText(c.label, c.align === "left" ? x + 12 : x + c.w / 2, y + headerH / 2);
        x += c.w;
      }
      y += headerH;
      // rows
      rows.forEach((r, i) => {
        ctx.fillStyle = i % 2 === 0 ? "#f7f7fb" : "#ffffff";
        ctx.fillRect(padX, y, w, rowH);
        x = padX;
        for (const c of cols) {
          ctx.fillStyle = c.align === "left" ? "#1a1a2e" : "#333344";
          ctx.font = (r.bold || c.key === "category" || c.key === "metric")
            ? "700 13px Segoe UI, Arial, sans-serif" : "13px Segoe UI, Arial, sans-serif";
          ctx.textAlign = c.align === "left" ? "left" : "center";
          ctx.fillText(String(r[c.key] ?? ""), c.align === "left" ? x + 12 : x + c.w / 2, y + rowH / 2);
          x += c.w;
        }
        y += rowH;
      });
      // footer (totals)
      if (footer) {
        ctx.fillStyle = "#ece8ff"; ctx.fillRect(padX, y, w, rowH);
        x = padX;
        for (const c of cols) {
          ctx.fillStyle = "#4a3aa0"; ctx.font = "700 13px Segoe UI, Arial, sans-serif";
          ctx.textAlign = c.align === "left" ? "left" : "center";
          ctx.fillText(String(footer[c.key] ?? ""), c.align === "left" ? x + 12 : x + c.w / 2, y + rowH / 2);
          x += c.w;
        }
        y += rowH;
      }
      ctx.strokeStyle = "#d4d4dc"; ctx.lineWidth = 1;
      ctx.strokeRect(padX, startY, w, y - startY);
      return y;
    };

    let cursorY = padY + titleBlock;
    cursorY = drawTable(catCols, categoryRows, cursorY, {
      category: "Total",
      total_patterns: catTotals.total_patterns,
      moved_qa: catTotals.moved_qa,
      moved_prod: catTotals.moved_prod,
    });

    // "Summary" label + summary table
    cursorY += gap - 12;
    ctx.fillStyle = "#1a1a2e"; ctx.textBaseline = "top"; ctx.textAlign = "left";
    ctx.font = "700 15px Segoe UI, Arial, sans-serif";
    ctx.fillText("Summary", padX, cursorY);
    cursorY += sumHeaderH;
    cursorY = drawTable(sumCols, sumRows, cursorY, null);

    // Comparison block (All Time only)
    if (comparison) {
      cursorY += gap - 12;
      ctx.fillStyle = "#1a1a2e"; ctx.textBaseline = "top"; ctx.textAlign = "left";
      ctx.font = "700 15px Segoe UI, Arial, sans-serif";
      ctx.fillText("Last 7 Days vs Previous 7 Days", padX, cursorY);
      cursorY += sumHeaderH;
      drawTable(compCols, compRows, cursorY, null);
    }

    const link = document.createElement("a");
    link.download = `error-tracking-report-${todayStr()}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }, [categoryRows, catTotals, metrics, period, comparison, upcomingCount]);

  const copyTotalsText = useCallback(async () => {
    const pad = (v, w, left) => (left ? String(v).padEnd(w) : String(v).padStart(w));
    const cc = [
      { key: "category", w: 14, left: true },
      { key: "total_patterns", w: 16 },
      { key: "moved_qa", w: 14 },
      { key: "moved_prod", w: 20 },
    ];
    const catLine = (r) => cc.map((c, i) => pad(r[c.key] ?? 0, c.w, c.left || i === 0)).join("  ");
    const lines = [];
    lines.push(`Error tracking report ${period.from && period.to ? `from ${period.from} to ${period.to}` : "(all time)"}`);
    lines.push("");
    lines.push(["Category".padEnd(14), "Total Patterns".padStart(16), "Moved to QA".padStart(14), "Moved to Production".padStart(20)].join("  "));
    lines.push("-".repeat(70));
    categoryRows.forEach((r) => lines.push(catLine(r)));
    lines.push("-".repeat(70));
    lines.push(catLine({ category: "Total", ...catTotals }));
    lines.push("");
    lines.push("Summary");
    lines.push(`  Total Patterns            : ${metrics.total_patterns}`);
    lines.push(`  Moved to QA               : ${metrics.moved_qa} / ${metrics.total_patterns}`);
    lines.push(`  Moved to Production       : ${metrics.moved_prod} / ${metrics.total_patterns}`);
    lines.push(`  In Development            : ${metrics.in_development}`);
    lines.push(`  Upcoming Deployment Plan  : ${upcomingCount}`);
    if (comparison) {
      lines.push("");
      lines.push("Last 7 Days vs Previous 7 Days (Error Patterns)");
      lines.push(`  Last 7 days (${comparison.last_7.from} to ${comparison.last_7.to})     : ${comparison.last_7.count}`);
      lines.push(`  Previous 7 days (${comparison.prev_7.from} to ${comparison.prev_7.to}) : ${comparison.prev_7.count}`);
      lines.push(`  Change                                       : ${comparison.change_pct > 0 ? "+" : ""}${comparison.change_pct}%`);
    }
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [categoryRows, catTotals, metrics, period, comparison, upcomingCount]);

  const hasData = members.length > 0;
  const hasConsolidated = categoryRows.length > 0 || metrics.total_patterns > 0;

  return (
    <div className="emr">
      <div className="emr-topbar">
        <div>
          <h1 className="emr-title"><FiAlertTriangle size={20} /> Error Handling by Member</h1>
          <p className="emr-subtitle">Who moved error patterns into Dev / QA / Production</p>
        </div>
        <div className="emr-actions">
          <div className="emr-exportgroup">
            <span className="emr-exportlabel">By member</span>
            <button className="emr-btn" onClick={copyText} disabled={!hasData} title="Copy member-wise report as text">
              <FiCopy size={14} /> Copy
            </button>
            <button className="emr-btn emr-btn--primary" onClick={downloadPng} disabled={!hasData} title="Download member-wise report as PNG">
              <FiDownload size={14} /> PNG
            </button>
          </div>
          <div className="emr-exportgroup">
            <span className="emr-exportlabel">Report (no member)</span>
            <button className="emr-btn" onClick={copyTotalsText} disabled={!hasConsolidated} title="Copy the category + summary report as text">
              <FiCopy size={14} /> Copy
            </button>
            <button className="emr-btn emr-btn--primary" onClick={downloadTotalsPng} disabled={!hasConsolidated} title="Download the category + summary report as PNG">
              <FiDownload size={14} /> PNG
            </button>
          </div>
          <div className="emr-exportgroup">
            <span className="emr-exportlabel">Share</span>
            <button className="emr-btn" onClick={copyShareLink} title="Copy a public link that previews as an image in WhatsApp / Teams">
              <FiLink size={14} /> Copy link
            </button>
            <a className="emr-btn" href={shareUrl} target="_blank" rel="noreferrer" title="Open the shareable image report">
              <FiExternalLink size={14} /> Preview
            </a>
          </div>
          {copied && <span className="emr-copied">Copied!</span>}
          <button className={`emr-refresh ${refreshing ? "emr-refresh--spin" : ""}`} onClick={() => fetchData(true)} title="Refresh">
            <FiRefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="emr-filters">
        <div className="emr-presets">
          {PRESETS.map((p) => (
            <button key={p.key} className={`emr-preset ${rangeMode === p.key ? "emr-preset--active" : ""}`} onClick={() => setRangeMode(p.key)}>
              {p.label}
            </button>
          ))}
        </div>

        {rangeMode === "custom" && (
          <div className="emr-daterange">
            <span className="emr-daterange__ico"><FiCalendar size={13} /></span>
            <input type="date" value={customFrom} max={customTo || todayStr()} onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="emr-daterange__sep">to</span>
            <input type="date" value={customTo} min={customFrom} max={todayStr()} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        )}

        <div className="emr-memberfilter" ref={memberBoxRef}>
          <button className={`emr-memberfilter__btn ${selectedMembers.length ? "emr-memberfilter__btn--active" : ""}`} onClick={() => setMemberOpen((o) => !o)}>
            <FiUsers size={13} /> {memberFilterLabel} <FiChevronDown size={13} />
          </button>
          {memberOpen && (
            <div className="emr-dropdown" onClick={(e) => e.stopPropagation()}>
              {availableMembers.length === 0 && <div className="emr-dropdown__empty">No members with activity</div>}
              {selectedMembers.length > 0 && (
                <div className="emr-dropdown__clear" onClick={() => setSelectedMembers([])}>Clear selection</div>
              )}
              {availableMembers.map((m) => {
                const mid = String(m.id);
                const checked = selectedMembers.includes(mid);
                return (
                  <label key={mid} className={`emr-opt ${checked ? "emr-opt--on" : ""}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleMember(mid)} />
                    <span>{m.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="emr-manualinput">
          <label htmlFor="emr-upcoming">Upcoming Deployment Plan</label>
          <input
            id="emr-upcoming"
            type="number"
            min="0"
            placeholder="0"
            value={upcoming}
            onChange={(e) => setUpcoming(e.target.value)}
          />
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="emr-loading"><div className="emr-spinner" /><span>Loading report…</span></div>
      ) : error ? (
        <div className="emr-empty emr-empty--error">
          <FiAlertTriangle size={38} /><h3>Couldn't load report</h3><p>{error}</p>
          <button className="emr-preset" onClick={() => fetchData()}>Retry</button>
        </div>
      ) : (
        <>
          {/* Headline metrics (consolidated) */}
          <div className="emr-kpis">
            <div className="emr-kpi emr-kpi--total"><div className="emr-kpi__icon"><FiActivity size={18} /></div><div className="emr-kpi__body"><span className="emr-kpi__value">{metrics.total_patterns}</span><span className="emr-kpi__label">Total Patterns</span></div></div>
            <div className="emr-kpi emr-kpi--qa"><div className="emr-kpi__icon"><FiCheckSquare size={18} /></div><div className="emr-kpi__body"><span className="emr-kpi__value">{metrics.moved_qa}</span><span className="emr-kpi__label">Moved to QA</span></div></div>
            <div className="emr-kpi emr-kpi--prod"><div className="emr-kpi__icon"><FiUploadCloud size={18} /></div><div className="emr-kpi__body"><span className="emr-kpi__value">{metrics.moved_prod}</span><span className="emr-kpi__label">Moved to Production</span></div></div>
            <div className="emr-kpi emr-kpi--dev"><div className="emr-kpi__icon"><FiCode size={18} /></div><div className="emr-kpi__body"><span className="emr-kpi__value">{metrics.in_development}</span><span className="emr-kpi__label">In Development</span></div></div>
            <div className="emr-kpi emr-kpi--uat"><div className="emr-kpi__icon"><FiClock size={18} /></div><div className="emr-kpi__body"><span className="emr-kpi__value">{upcomingCount}</span><span className="emr-kpi__label">Upcoming Deployment</span></div></div>
          </div>

          {/* All-time comparison: last 7 days vs previous 7 days (patterns) */}
          {comparison && (
            <div className="emr-card emr-compare">
              <div className="emr-card__header">
                <h3><FiActivity size={14} /> Last 7 Days vs Previous 7 Days — Error Patterns</h3>
              </div>
              <div className="emr-compare__body">
                <div className="emr-compare__box">
                  <span className="emr-compare__label">Last 7 days</span>
                  <span className="emr-compare__range">{comparison.last_7.from} → {comparison.last_7.to}</span>
                  <span className="emr-compare__value">{comparison.last_7.count}</span>
                </div>
                <div className="emr-compare__vs">
                  <span className={`emr-compare__delta ${comparison.change_pct > 0 ? "up" : comparison.change_pct < 0 ? "down" : ""}`}>
                    {comparison.change_pct > 0 ? <FiTrendingUp size={16} /> : comparison.change_pct < 0 ? <FiTrendingDown size={16} /> : null}
                    {comparison.change_pct > 0 ? "+" : ""}{comparison.change_pct}%
                  </span>
                  <span className="emr-compare__vslabel">vs previous</span>
                </div>
                <div className="emr-compare__box">
                  <span className="emr-compare__label">Previous 7 days</span>
                  <span className="emr-compare__range">{comparison.prev_7.from} → {comparison.prev_7.to}</span>
                  <span className="emr-compare__value emr-compare__value--muted">{comparison.prev_7.count}</span>
                </div>
              </div>
            </div>
          )}

          {/* Consolidated (no-member) report: category + summary */}
          {hasConsolidated ? (
            <div className="emr-card">
              <div className="emr-card__header"><h3><FiLayers size={14} /> Error tracking report {periodTitle}</h3></div>
              <div className="emr-tablewrap">
                <table className="emr-table">
                  <thead>
                    <tr><th>Category</th><th>Total Patterns</th><th>Moved to QA</th><th>Moved to Production</th></tr>
                  </thead>
                  <tbody>
                    {categoryRows.map((r) => (
                      <tr key={r.category}>
                        <td className="emr-table__name">{r.category}</td>
                        <td>{r.total_patterns}</td>
                        <td>{r.moved_qa}</td>
                        <td>{r.moved_prod}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="emr-table__name">Total</td>
                      <td className="emr-table__total">{catTotals.total_patterns}</td>
                      <td className="emr-table__total">{catTotals.moved_qa}</td>
                      <td className="emr-table__total">{catTotals.moved_prod}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="emr-card__subhead">Summary</div>
              <div className="emr-tablewrap">
                <table className="emr-table emr-table--metric">
                  <thead><tr><th>Metric</th><th>Count</th></tr></thead>
                  <tbody>
                    <tr><td className="emr-table__name">Total Patterns</td><td>{metrics.total_patterns}</td></tr>
                    <tr><td className="emr-table__name">Moved to QA</td><td>{metrics.moved_qa} / {metrics.total_patterns}</td></tr>
                    <tr><td className="emr-table__name">Moved to Production</td><td>{metrics.moved_prod} / {metrics.total_patterns}</td></tr>
                    <tr><td className="emr-table__name emr-table__strong">In Development</td><td className="emr-table__strong">{metrics.in_development}</td></tr>
                    <tr><td className="emr-table__name emr-table__strong">Upcoming Deployment Plan</td><td className="emr-table__strong">{upcomingCount}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="emr-empty">
              <FiLayers size={38} /><h3>No patterns in this period</h3>
              <p>No error patterns were found for the selected filters. Try a wider range.</p>
            </div>
          )}

          {/* By-member breakdown */}
          {hasData && (
            <>
              <div className="emr-card emr-chart">
                <div className="emr-card__header"><h3><FiUsers size={14} /> Moves per Member</h3></div>
                <BarChart
                  height={300}
                  xAxis={[{ scaleType: "band", data: chart.labels }]}
                  series={chart.series}
                  slotProps={{ legend: { direction: "row", position: { vertical: "top", horizontal: "middle" } } }}
                  sx={{
                    "& .MuiChartsAxis-tickLabel": { fill: "var(--text-muted)" },
                    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "var(--border-subtle)" },
                    "& .MuiChartsLegend-series text": { fill: "var(--text-primary) !important" },
                  }}
                />
              </div>

              <div className="emr-card">
                <div className="emr-card__header"><h3><FiUsers size={14} /> By Member — {periodLabel}</h3></div>
                <div className="emr-tablewrap">
                  <table className="emr-table">
                    <thead>
                      <tr><th>Member</th><th>In Dev</th><th>In QA</th><th>Production</th><th>Total</th></tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr key={m.member_id}>
                          <td className="emr-table__name">{m.member_name}</td>
                          <td>{m.in_dev}</td>
                          <td>{m.in_qa}</td>
                          <td>{m.production}</td>
                          <td className="emr-table__total">{m.total}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="emr-table__name">TOTAL</td>
                        <td>{summary.in_dev}</td>
                        <td>{summary.in_qa}</td>
                        <td>{summary.production}</td>
                        <td className="emr-table__total">{summary.total}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ErrorMemberReport;
